import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getAllCourses } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

// How many courses the homepage teaser shows before the "view all" button.
const HOME_COURSE_LIMIT = 6;

const BRAND_RED = '#DF3E30';

// Cards follow the theme radius token (shape.borderRadius) rather than a local
// magic number, so the corner treatment stays in step with the rest of the app.
const cardRadius = (theme) => theme.shape.borderRadius;

// Reveal timing: a short spring reads as "settling in" rather than a slow
// fade, and the stagger stays under half a second so the row never feels late.
const REVEAL_TRANSITION = { duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] };
const revealDelay = (index) => Math.min(index, 5) * 0.07;

const StyledMedia = styled('div')({
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: '3 / 2',
  bgcolor: 'grey.200',
  '@media (min-width: 600px)': {
    aspectRatio: '16 / 10',
  },
});

const StyledCover = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  position: 'absolute',
  inset: 0,
  transition: 'transform 0.7s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
});

// Light scrim: just enough contrast for the tag pill to stay legible over pale
// artwork, without visibly dulling the image the way a heavy overlay did.
const MediaScrim = styled('div')({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background: (theme) =>
    `linear-gradient(180deg, ${alpha(theme.palette.grey[900], 0.18)} 0%, ${alpha(
      theme.palette.grey[900],
      0
    )} 38%, ${alpha(theme.palette.grey[900], 0.45)} 100%)`,
  opacity: { xs: 1, md: 0.85 },
  transition: 'opacity 0.4s ease',
  '@media (hover: hover)': {
    '.course-card:hover &': { opacity: 1 },
  },
});

const TagPill = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(2),
  bottom: theme.spacing(2),
  zIndex: 2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  maxWidth: 'calc(100% - 32px)',
  height: 34,
  paddingRight: theme.spacing(1.75),
  paddingLeft: 5,
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.92),
  boxShadow: `0 4px 14px ${alpha(theme.palette.grey[900], 0.22)}`,
  backdropFilter: 'blur(6px)',
  transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  '@media (hover: hover)': {
    '.course-card:hover &': { transform: 'translateY(-2px)' },
  },
}));

const TagName = styled(Typography)({
  overflow: 'hidden',
  fontWeight: 700,
  fontSize: 11,
  lineHeight: 1,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: 'text.primary',
});

// The title owns the single link for the whole card. It must stay
// `position: static` so its `::after` resolves against the card and stretches
// over the entire tile rather than just the heading text.
const StyledTitle = styled(RouterLink)(({ theme }) => ({
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  color: 'inherit',
  textDecoration: 'none',
  transition: 'color 0.25s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 3,
  },
  '&:hover': { color: theme.palette.primary.main },
  '&:focus-visible::after': {
    content: '""',
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -4,
    borderRadius: theme.shape.borderRadius,
  },
}));

// The visible call to action. Decorative (`pointerEvents: 'none'`) because the
// stretched title link already covers the card, so this only needs to read as
// the destination — the same "View course" pattern the courses page uses.
const CardAction = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(2),
  color: theme.palette.primary.main,
  pointerEvents: 'none',
}));

const CardActionArrow = styled(Iconify)({
  transition: 'transform 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  '@media (hover: hover)': {
    '.course-card:hover &': { transform: 'translateX(5px)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

// ----------------------------------------------------------------------

function CourseCard({ course, index = 0 }) {
  const { slug, name, introduction, image, tag, subjectCount, videoCount } = course ?? {};
  const linkTo = `/course/${slug}`;

  // Only render the metrics the payload actually provides, so a partial
  // response never shows "undefined lessons".
  const stats = [
    { id: 'lessons', icon: 'ic:round-menu-book', value: subjectCount, label: 'lessons' },
    { id: 'videos', icon: 'ic:round-play-circle', value: videoCount, label: 'videos' },
  ].filter((stat) => Number.isFinite(stat.value));

  return (
    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ ...REVEAL_TRANSITION, delay: revealDelay(index) }}
        sx={{ width: '100%', display: 'flex' }}
      >
        <Card
          className="course-card"
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: cardRadius,
            overflow: 'hidden',
            boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.grey[900], 0.08)}`,
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 4,
              width: '100%',
              height: 3,
              backgroundColor: BRAND_RED,
              transform: 'scaleX(0)',
              transformOrigin: 'left center',
              transition: 'transform 0.45s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
            },
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 20px 40px ${alpha(theme.palette.grey[900], 0.16)}`,
              },
              '&:hover::before': { transform: 'scaleX(1)' },
              '&:hover img': { transform: 'scale(1.07)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
              '&::before': { transition: 'none' },
              '&:hover': { transform: 'none' },
              '&:hover img': { transform: 'none' },
            },
          }}
        >
          <StyledMedia>
            {image ? (
              <StyledCover src={image} alt={name} loading="lazy" decoding="async" />
            ) : (
              <Stack sx={{ alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0, color: 'text.disabled' }}>
                <Iconify icon="ic:round-menu-book" width={40} />
              </Stack>
            )}

            <MediaScrim />

            {tag?.name && (
              <TagPill>
                <Avatar
                  alt=""
                  src={tag.image}
                  sx={{ width: 24, height: 24, bgcolor: 'grey.300', color: 'common.white', fontSize: 11 }}
                >
                  {tag.name.charAt(0)}
                </Avatar>
                <TagName>{tag.name}</TagName>
              </TagPill>
            )}
          </StyledMedia>

          <CardContent
            sx={{
              pt: 2.5,
              pb: 2.5,
              px: { xs: 2, sm: 2.5 },
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <StyledTitle
              component="h3"
              variant="h6"
              to={linkTo}
              aria-label={`View course: ${name}`}
              sx={{ fontSize: { xs: 17, sm: 18 }, lineHeight: 1.35, mb: 1.25 }}
            >
              {name}
            </StyledTitle>

            <Divider sx={{ mb: 1.25 }} />

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: 13.5, sm: 14 },
                lineHeight: 1.6,
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {introduction}
            </Typography>

            {stats.length > 0 && (
              <Stack
                direction="row"
                spacing={{ xs: 1.5, sm: 2.5 }}
                 sx={{ mt: 'auto', pt: 2, flexWrap: 'wrap' }}
                useFlexGap
              >
                {stats.map((stat) => (
                   <Stack key={stat.id} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Iconify icon={stat.icon} width={18} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                      {`${stat.value} ${stat.label}`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            <CardAction>
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                View course
              </Typography>
              <CardActionArrow icon="eva:arrow-forward-outline" width={16} />
            </CardAction>
          </CardContent>
        </Card>
      </MotionInView>
    </Grid>
  );
}

CourseCard.propTypes = {
  course: PropTypes.object.isRequired,
  index: PropTypes.number,
};

// ----------------------------------------------------------------------

function CourseCardSkeleton() {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} sx={{ display: 'flex' }}>
      <Skeleton
        variant="rounded"
        width="100%"
        sx={{ height: { xs: 380, sm: 400 }, borderRadius: cardRadius }}
      />
    </Grid>
  );
}

CourseWidget.propTypes = {
  // Set by the homepage, where `getHomeBundle` already filled `courses` with
  // this same list. Left off elsewhere: the courses page renders the full set,
  // not the six this widget caps itself to.
  skipFetch: PropTypes.bool,
};

export default function CourseWidget({ skipFetch = false }) {
  const dispatch = useDispatch();
  const { courses, error, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    if (!skipFetch) {
      dispatch(getAllCourses());
    }
  }, [dispatch, skipFetch]);

  // Tolerate either a bare array or the `{ results: [...] }` wrapper.
  const list = useMemo(
    () => (Array.isArray(courses) ? courses : courses?.results ?? []).filter(Boolean),
    [courses]
  );

  if (!list.length && isLoading) {
    return (
      <Grid container spacing={{ xs: 2.5, sm: 3 }}>
        {Array.from({ length: 6 }, (_, index) => (
          <CourseCardSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  if (!list.length) {
    return (
      <Card sx={{ py: { xs: 6, md: 8 }, px: 3, textAlign: 'center', borderRadius: cardRadius }}>
        <Stack spacing={1} sx={{ alignItems: 'center' }}>
          {isLoading ? (
            <CircularProgress size={28} thickness={4} />
          ) : (
            <Iconify icon="ic:round-menu-book" width={32} sx={{ color: 'text.disabled' }} />
          )}
          <Typography component="h3" variant="h6" sx={{ color: 'text.secondary' }}>
            Courses coming soon
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 420, mx: 'auto' }}>
            {error
              ? 'We could not load the course list right now. Please try again later.'
              : 'New courses will be published here soon.'}
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Grid container spacing={{ xs: 2.5, sm: 3 }} sx={{ alignItems: 'stretch' }}>
      {list.slice(0, HOME_COURSE_LIMIT).map((course, index) => (
        <CourseCard key={course?.slug ?? index} course={course} index={index} />
      ))}
    </Grid>
  );
}
